import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './App.css';
import { useWallet } from './contexts/WalletContext';
import JSZip from 'jszip';

// ============================================================================
// API CONFIGURATION - Update these values when your server is ready
// ============================================================================
// API_BASE_URL: Currently set to 'https://portalapply.online'
// API_ENDPOINT: Should remain '/api/listings' unless you change the backend
// API_KEY: Your API key for authentication
// ============================================================================

// Import new layer filter components
import LayerBar from './components/rarity/LayerBar';
import VariantPicker from './components/rarity/VariantPicker';
import LivePreview from './components/rarity/LivePreview';
import FilterChips from './components/rarity/FilterChips';
import NFTCard from './components/NFTCard';
import UserNFTs from './components/UserNFTs';
import { loadLayers } from './utils/loadLayers';


// Import your score data
import { score_data } from './data.js';

// Import the corrected NFT tokens data (the "bible")
import correctedNFTData from './corrected_nft_people_tokens.json';
// Import the mapped attributes data for layer filtering
import mappedAttributesData from './mapped_attributes.json';

// Local NFT images - no longer used since we fetch from API



function Rarity() {
  const { userAddress, isConnected, connectWallet, disconnectWallet } = useWallet();
  const [activeTab, setActiveTab] = useState('collection'); // 'collection' or 'my-nfts'
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Starting...');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('rank');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // New layer filter states (UI-only, no logic changes)
  const [activeLayer, setActiveLayer] = useState('back');
  const [layerUI, setLayerUI] = useState({});
  const [soloLayer, setSoloLayer] = useState(null);
  const [onlyShowCompatible, setOnlyShowCompatible] = useState(false);
  const [layerHistory, setLayerHistory] = useState([]);
  const [hoveredVariant, setHoveredVariant] = useState(null);

  // Track whether price data is available from API
  const [priceDataAvailable, setPriceDataAvailable] = useState(false);
  const [priceData, setPriceData] = useState({});
  const [priceDataLoading, setPriceDataLoading] = useState(false);

  // Price Impact Simulator states
  const [priceImpactTake, setPriceImpactTake] = useState(1);
  const [priceImpactResult, setPriceImpactResult] = useState(null);
  const [priceImpactLoading, setPriceImpactLoading] = useState(false);


  const itemsPerPage = 50;

  // Utility function to format XTZ values
  const formatXTZ = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '0 XTZ';
    return `${value.toFixed(2)} XTZ`;
  };

  // Price Impact Calculation function
  const handlePriceImpactCalculate = async () => {
    if (!priceDataAvailable || Object.keys(priceData).length === 0) {
      alert('Price data not available. Please wait for data to load.');
      return;
    }

    setPriceImpactLoading(true);
    setPriceImpactResult(null);

    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      // Get all NFTs with prices, sorted by price
      const nftsWithPrices = nfts
        .filter(nft => nft.hasPrice && nft.priceValue > 0)
        .sort((a, b) => a.priceValue - b.priceValue);

      if (nftsWithPrices.length === 0) {
        alert('No NFTs with prices found.');
        return;
      }

      const take = Math.min(priceImpactTake, nftsWithPrices.length);
      const selectedNFTs = nftsWithPrices.slice(0, take);
      
      if (selectedNFTs.length === 0) {
        alert('No NFTs available for the selected quantity.');
        return;
      }

      // Calculate metrics
      const floorPrice = selectedNFTs[0].priceValue;
      const avgPrice = selectedNFTs.reduce((sum, nft) => sum + nft.priceValue, 0) / selectedNFTs.length;
      const totalCost = selectedNFTs.reduce((sum, nft) => sum + nft.priceValue, 0);
      const impact = ((avgPrice - floorPrice) / floorPrice) * 100;
      const savings = (floorPrice * take) - totalCost;

      const result = {
        impact: impact.toFixed(1),
        avgPrice: avgPrice.toFixed(2),
        totalCost: totalCost.toFixed(2),
        savings: savings.toFixed(2)
      };

      setPriceImpactResult(result);
      
    } catch (error) {
      console.error('❌ Error in price impact calculation:', error);
      alert('Error calculating price impact. Please try again.');
    } finally {
      setPriceImpactLoading(false);
    }
  };

  // Fetch NFT prices from new API endpoint
  const fetchNFTPrices = useCallback(async () => {
    try {
      setPriceDataLoading(true);
      
      // New API configuration
      const API_BASE_URL = 'https://portalapply.online';
      const API_ENDPOINT = '/api/v1/listings/active';
      const PAGE_SIZE = 100;
      
      let allListings = [];
      let hasMoreData = true;
      let nextCursor = null;
      
      // First API call
      let apiUrl = `${API_BASE_URL}${API_ENDPOINT}?sort=price_asc&limit=${PAGE_SIZE}`;
      
      while (hasMoreData) {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          mode: 'cors'
        });

        if (!response.ok) {
          let errorBody = '';
          try {
            errorBody = await response.text();
          } catch (e) {
            // Could not read error response body
          }
          
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        
        if (!contentType || !contentType.includes('application/json')) {
          const responseText = await response.text();
          throw new Error(`API returned non-JSON response: ${contentType}`);
        }

        const result = await response.json();
        
        // Handle the new API response structure
        if (result && result.items && Array.isArray(result.items)) {
          const pageData = result.items;
          
          if (pageData.length === 0) {
            hasMoreData = false;
          } else {
            // Add this page's data to our collection
            allListings = allListings.concat(pageData);
            
            // Check if there are more pages
            if (result.hasMore && result.nextCursor) {
              nextCursor = result.nextCursor;
              apiUrl = `${API_BASE_URL}${API_ENDPOINT}?sort=price_asc&limit=${PAGE_SIZE}&cursor=${nextCursor}`;
            } else {
              hasMoreData = false;
            }
          }
        } else {
          hasMoreData = false;
        }
        
        // Add a small delay between requests
        if (hasMoreData) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (allListings.length > 0) {
        // Create a map of token_id to price data from all listings
        const priceMap = {};
        allListings.forEach(listing => {
          if (listing.token_id && listing.price !== null && listing.price !== undefined) {
            // Convert from mutez to tez (divide by 1,000,000)
            const priceInTez = (listing.price / 1000000).toFixed(2);
            priceMap[listing.token_id] = {
              price: `${priceInTez} tez`,
              priceValue: parseFloat(priceInTez),
              hasPrice: true
            };
          }
        });
        
        setPriceData(priceMap);
        setPriceDataAvailable(true);
      } else {
        setPriceDataAvailable(false);
      }
    } catch (error) {
      console.error('❌ Failed to fetch NFT prices from new API:', error.message);
      
      // Check if it's a network/server error
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.warn('🌐 Network error - New API server may not be running on localhost:3001');
      } else if (error.message.includes('non-JSON response')) {
        console.warn('📄 New API returned HTML instead of JSON - check if endpoint is correct');
      }
      
      setPriceDataAvailable(false);
      
      // Set empty price data so NFTs show as "Held"
      setPriceData({});
    } finally {
      setPriceDataLoading(false);
    }
  }, []);

  // Combine corrected NFT data with score data
  const combineCorrectedData = (correctedNFTs, scoreData) => {
    
    // Create score map
    const scoreMap = new Map();
    scoreData.forEach(item => {
      scoreMap.set(item.Item, { score: item.Score, rank: item.Rank, index: item.Index });
    });
    
    return correctedNFTs.map(nft => {
      const scoreInfo = scoreMap.get(nft.name) || { score: 0, rank: 9999, index: 9999 };
      
      // Use display_uri from corrected_nft_people_tokens.json
      const display_uri = nft.display_uri;
      
      // Get price from API data if available
      // The price data is keyed by token_id from API, but nft.id might be different
      // Try both nft.id and extract token number from name as fallback
      const tokenNumber = nft.name.match(/#(\d+)/)?.[1];
      const nftPriceInfo = priceData[nft.id] || (tokenNumber ? priceData[tokenNumber] : null) || { price: " - ", hasPrice: false, priceValue: 0 };
      
      
      return {
        id: nft.id,
        title: nft.name,
        display_uri: display_uri,
        score: scoreInfo.score,
        rank: scoreInfo.rank,
        index: scoreInfo.index,
        price: nftPriceInfo.price,
        hasPrice: nftPriceInfo.hasPrice,
        priceValue: nftPriceInfo.priceValue,
        marketplace: "hen",
        swapId: null,
        askId: null,
        swaps: []
      };
    });
  };



  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setLoadingMessage('🔄 Loading NFT data...');
      
      try {
        // Use corrected NFT data directly for images
        setLoadingMessage('📚 Loading from corrected NFT data...');
        const correctedNFTs = correctedNFTData;
        const combinedData = combineCorrectedData(correctedNFTs, score_data);
        setNfts(combinedData);
        setLoadingMessage(`📊 Loaded ${combinedData.length} NFTs with images`);
        
        // Fetch price data from API
        await fetchNFTPrices();
        
      } catch (error) {
        console.error('❌ Data loading failed:', error);
        setLoadingMessage('❌ Failed to load NFT data');
      }
      
      setLoading(false);
    };

    loadData();
  }, []);

  // Update NFT data when price data becomes available
  useEffect(() => {
    if (priceDataAvailable && Object.keys(priceData).length > 0) {
      const updatedNfts = combineCorrectedData(correctedNFTData, score_data);
      setNfts(updatedNfts);
    }
  }, [priceDataAvailable, priceData, correctedNFTData, score_data]);

  // Filter and sort NFTs
  const filteredAndSortedNfts = nfts
    .filter(nft => {
      // Search filter
      const matchesSearch = nft.title.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Additional filters
      let matchesFilter = true;
      switch (filterBy) {
        case 'withPrice':
          matchesFilter = priceDataAvailable && nft.hasPrice;
          break;
        case 'withoutPrice':
          matchesFilter = priceDataAvailable && !nft.hasPrice;
          break;
        case 'top100':
          matchesFilter = nft.rank <= 100;
          break;
        case 'top500':
          matchesFilter = nft.rank <= 500;
          break;
        case 'rare':
          matchesFilter = nft.rank <= 50;
          break;
        default:
          matchesFilter = true;
      }
      
      // Layer filter logic using mapped_attributes.json
      let matchesLayerFilter = true;
      
      // Check if any layer filters are active (now including 'include' and 'exclude' states)
      const hasActiveFilters = Object.keys(layerUI).some(layerKey => 
        layerUI[layerKey] && Object.values(layerUI[layerKey]).some(value => value === 'only' || value === 'include' || value === 'exclude')
      );
      
      if (hasActiveFilters) {
        
        // Find the NFT in mapped_attributes.json by its code
        const nftCode = nft.title.match(/#(\d+)/)?.[1];
        if (nftCode) {
          // Pad the code to 4 digits to match the format in mapped_attributes.json
          const paddedCode = nftCode.padStart(4, '0');
          
          // Find the NFT attributes from mapped_attributes.json
          const nftAttributes = mappedAttributesData.find(item => item.Code === paddedCode);
          
          if (nftAttributes) {
            // Check each active layer filter
            for (const [layerKey, valueMap] of Object.entries(layerUI)) {
              if (valueMap) {
                // Get the attribute value for this layer from the NFT
                const layerKeyMapping = {
                  'back': 'Back',
                  'eyes': 'Eyes',
                  'hairs': 'Hairs',
                  'mouth': 'Mouth',
                  'neck': 'Neck',
                  'noses': 'Noses'
                };
                const attributeKey = layerKeyMapping[layerKey];
                const attributeValue = nftAttributes[attributeKey];
                
                // Check if this layer has any active filters
                const hasActiveFiltersInLayer = Object.values(valueMap).some(value => 
                  value === 'only' || value === 'include' || value === 'exclude'
                );
                
                if (hasActiveFiltersInLayer) {
                  let layerMatches = false;
                  let layerExcluded = false;
                  
                  // Process 'only' filters first (highest priority)
                  const onlyVariants = Object.entries(valueMap)
                    .filter(([_, state]) => state === 'only')
                    .map(([variantId, _]) => variantId);
                  
                  if (onlyVariants.length > 0) {
                    // If there are 'only' filters, NFT must match one of them
                    layerMatches = onlyVariants.includes(attributeValue);
                  } else {
                    // Check 'include' filters (OR logic - match any)
                    const includeVariants = Object.entries(valueMap)
                      .filter(([_, state]) => state === 'include')
                      .map(([variantId, _]) => variantId);
                    
                    if (includeVariants.length > 0) {
                      layerMatches = includeVariants.includes(attributeValue);
                    }
                  }
                  
                  // Check 'exclude' filters (if NFT has excluded variant, hide it)
                  const excludeVariants = Object.entries(valueMap)
                    .filter(([_, state]) => state === 'exclude')
                    .map(([variantId, _]) => variantId);
                  
                  if (excludeVariants.includes(attributeValue)) {
                    layerExcluded = true;
                  }
                  
                  // If this layer has filters but none match, or if excluded, fail the filter
                  if (!layerMatches || layerExcluded) {
                    matchesLayerFilter = false;
                    break;
                  }
                }
              }
            }
          }
        }
      }
      
      return matchesSearch && matchesFilter && matchesLayerFilter;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'rank':
          comparison = a.rank - b.rank;
          break;
        case 'score':
          comparison = b.score - a.score;
          break;
        case 'name':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'price':
          if (!priceDataAvailable) {
            // If price data not available, fall back to rank sorting
            comparison = a.rank - b.rank;
          } else if (a.hasPrice && b.hasPrice) {
            const priceA = parseFloat(a.price.replace(' tez', ''));
            const priceB = parseFloat(b.price.replace(' tez', ''));
            comparison = priceA - priceB;
          } else if (a.hasPrice) {
            comparison = -1;
          } else if (b.hasPrice) {
            comparison = 1;
          } else {
            // Both have no price, sort by rank
            comparison = a.rank - b.rank;
          }
          break;
        default:
          comparison = a.rank - b.rank;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedNfts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNfts = filteredAndSortedNfts.slice(startIndex, endIndex);

  // Layer filter functions (UI-only, no logic changes)
  const handleLayerChange = (layerKey) => {
    setActiveLayer(layerKey);
  };

  const handleVariantChange = (layerKey, newValueMap) => {
    // Save to history for undo
    const newHistory = [...layerHistory, { layerKey, valueMap: { ...layerUI[layerKey] } }];
    if (newHistory.length > 10) newHistory.shift(); // Keep max 10 steps
    setLayerHistory(newHistory);
    
    setLayerUI(prev => ({
      ...prev,
      [layerKey]: newValueMap
    }));
  };

  const handleRemoveFilter = (layerKey, variantId) => {
    setLayerUI(prev => ({
      ...prev,
      [layerKey]: {
        ...prev[layerKey],
        [variantId]: null
      }
    }));
  };

  const handleClearAll = () => {
    setLayerHistory([]);
    setLayerUI({});
  };

  const handleUndo = () => {
    if (layerHistory.length > 0) {
      const lastAction = layerHistory[layerHistory.length - 1];
      setLayerUI(prev => ({
        ...prev,
        [lastAction.layerKey]: lastAction.valueMap
      }));
      setLayerHistory(prev => prev.slice(0, -1));
    }
  };

  const handleToggleSolo = () => {
    setSoloLayer(prev => prev ? null : activeLayer);
  };

  const handleToggleOnlyCompat = () => {
    setOnlyShowCompatible(prev => !prev);
  };

  // Prepare composed layers for preview
  const layers = loadLayers();
  const composedLayers = Object.keys(layers).reduce((acc, layerKey) => {
    const layerVariants = layers[layerKey] || [];
    acc[layerKey] = {
      firstVariant: layerVariants[0]?.src,
      selectedVariant: layerUI[layerKey] ? 
        Object.entries(layerUI[layerKey])
          .filter(([_, state]) => state === 'only')
          .map(([id, _]) => layerVariants.find(v => v.id === id)?.src)
          .filter(Boolean)[0] || null : null
    };
    return acc;
  }, {});

  // Download function for current page images
  const downloadCurrentPageImages = async () => {
    if (currentNfts.length === 0) return;
    
    try {
      console.log(`📥 Starting download of ${currentNfts.length} NFT images...`);
      
      // Create a zip file using JSZip
      const zip = new JSZip();
      
      // Track download progress
      let downloadedCount = 0;
      let failedCount = 0;
      
      // Download each image and add to zip
      const downloadPromises = currentNfts.map(async (nft, index) => {
        try {
          const imageUrl = getImageUrl(nft);
          const nftNumber = nft.title.match(/#(\d+)/)?.[1] || nft.id;
          
          // Skip placeholder images (data URLs)
          if (imageUrl.startsWith('data:')) {
            console.log(`⏭️ Skipping placeholder for NFT #${nftNumber}`);
            return;
          }
          
          // Fetch the image
          const response = await fetch(imageUrl);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const blob = await response.blob();
          
          // Add to zip with descriptive filename
          const filename = `NFT_People_${nftNumber.padStart(4, '0')}_${nft.rank}_${nft.score}.png`;
          zip.file(filename, blob);
          
          downloadedCount++;
          console.log(`✅ Downloaded NFT #${nftNumber} (${downloadedCount}/${currentNfts.length})`);
          
    } catch (error) {
          failedCount++;
          console.error(`❌ Failed to download NFT #${nft.title}:`, error);
        }
      });
      
      // Wait for all downloads to complete
      await Promise.all(downloadPromises);
      
      console.log(`📊 Download summary: ${downloadedCount} successful, ${failedCount} failed`);
      
      if (downloadedCount > 0) {
        // Generate and download the zip file
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const downloadUrl = URL.createObjectURL(zipBlob);
        
        // Create download link
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `NFT_People_Page_${currentPage}_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        URL.revokeObjectURL(downloadUrl);
        
        console.log(`🎉 Zip file downloaded successfully!`);
      } else {
        console.warn('⚠️ No images were downloaded successfully');
      }
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      alert('Failed to download images. Please try again.');
    }
  };

  // Helper function to count NFTs matching current layer filters
  const getLayerFilteredCount = useCallback(() => {
    const hasActiveFilters = Object.keys(layerUI).some(layerKey => 
      layerUI[layerKey] && Object.values(layerUI[layerKey]).some(value => 
        value === 'only' || value === 'include' || value === 'exclude'
      )
    );
    
    if (!hasActiveFilters) return nfts.length;
    
    return nfts.filter(nft => {
      const nftCode = nft.title.match(/#(\d+)/)?.[1];
      if (!nftCode) return false;
      
      const paddedCode = nftCode.padStart(4, '0');
      const nftAttributes = mappedAttributesData.find(item => item.Code === paddedCode);
      
      if (!nftAttributes) return false;
      
      // Check each active layer filter
      for (const [layerKey, valueMap] of Object.entries(layerUI)) {
        if (valueMap) {
          // Get the attribute value for this layer from the NFT
          const layerKeyMapping = {
            'back': 'Back',
            'eyes': 'Eyes',
            'hairs': 'Hairs',
            'mouth': 'Mouth',
            'neck': 'Neck',
            'noses': 'Noses'
          };
          const attributeKey = layerKeyMapping[layerKey];
          const attributeValue = nftAttributes[attributeKey];
          
          // Check if this layer has any active filters
          const hasActiveFiltersInLayer = Object.values(valueMap).some(value => 
            value === 'only' || value === 'include' || value === 'exclude'
          );
          
          if (hasActiveFiltersInLayer) {
            let layerMatches = false;
            let layerExcluded = false;
            
            // Process 'only' filters first (highest priority)
            const onlyVariants = Object.entries(valueMap)
              .filter(([_, state]) => state === 'only')
              .map(([variantId, _]) => variantId);
            
            if (onlyVariants.length > 0) {
              // If there are 'only' filters, NFT must match one of them
              layerMatches = onlyVariants.includes(attributeValue);
            } else {
              // Check 'include' filters (OR logic - match any)
              const includeVariants = Object.entries(valueMap)
                .filter(([_, state]) => state === 'include')
                .map(([variantId, _]) => variantId);
              
              if (includeVariants.length > 0) {
                layerMatches = includeVariants.includes(attributeValue);
              }
            }
            
            // Check 'exclude' filters (if NFT has excluded variant, hide it)
            const excludeVariants = Object.entries(valueMap)
              .filter(([_, state]) => state === 'exclude')
              .map(([variantId, _]) => variantId);
            
            if (excludeVariants.includes(attributeValue)) {
              layerExcluded = true;
            }
            
            // If this layer has filters but none match, or if excluded, fail the filter
            if (!layerMatches || layerExcluded) {
              return false;
            }
          }
        }
      }
      return true;
    }).length;
  }, [layerUI, nfts]);



  // Listen for variant hover events from VariantPicker
  useEffect(() => {
    const handleVariantHover = (event) => {
      setHoveredVariant(event.detail);
    };

    window.addEventListener('variantHover', handleVariantHover);
    return () => window.removeEventListener('variantHover', handleVariantHover);
  }, []);





  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (newSortBy) => {
    // If trying to sort by price but price data not available, fall back to rank
    if (newSortBy === 'price' && !priceDataAvailable) {
      setSortBy('rank');
      setSortOrder('asc');
      return;
    }
    
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const handleFilterChange = (newFilterBy) => {
    // If trying to filter by price but price data not available, fall back to all
    if ((newFilterBy === 'withPrice' || newFilterBy === 'withoutPrice') && !priceDataAvailable) {
      setFilterBy('all');
      return;
    }
    
    setFilterBy(newFilterBy);
  };

  // Handle image loading errors
  const handleImageError = (e, nftTitle) => {
    // Use the same stable placeholder to prevent flickering
    const nftId = e.target.alt.match(/#(\d+)/)?.[1] || 'Unknown';
    e.target.src = `data:image/svg+xml;base64,${btoa(`
      <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="200" fill="#333"/>
        <text x="100" y="100" font-family="Arial" font-size="16" fill="#666" text-anchor="middle" dy=".3em">NFT ${nftId}</text>
      </svg>
    `)}`;
  };

  // Get proper image URL - exactly like your old system
  const getImageUrl = useCallback((nft) => {
    // Extract NFT number from title for better placeholders
    const nftNumber = nft.title?.match(/#(\d+)/)?.[1] || nft.id;
    
    if (nft.display_uri) {
      // Convert ipfs:// to https://ipfs.io/ipfs/ exactly like your old code
      if (nft.display_uri.startsWith('ipfs://')) {
        const ipfsUrl = nft.display_uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
        return ipfsUrl;
      }
      
      // Handle other URI formats
      return nft.display_uri;
    }
    
    // Generate beautiful placeholder for NFTs without display_uri
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="200" fill="#1a1a1a"/>
        <rect width="180" height="180" x="10" y="10" fill="#2a2a2a" rx="15"/>
        <circle cx="100" cy="70" r="25" fill="#4a4a4a"/>
        <text x="100" y="78" font-family="Arial" font-size="12" fill="#888" text-anchor="middle">NFT People</text>
        <text x="100" y="120" font-family="Arial" font-size="28" fill="#fff" text-anchor="middle" font-weight="bold">#${nftNumber}</text>
        <text x="100" y="150" font-family="Arial" font-size="10" fill="#666" text-anchor="middle">No Image</text>
      </svg>
    `)}`;
  }, []);

  // Remove loading spinner - show NFTs immediately
  return (
    <div className="rarity-container">
      {/* Main Header - No duplicate navigation */}
      {/* <div className="rarity-header">
        <h1 className="rarity-title">Rarity Tool</h1>

      </div> */}

      {/* Wallet Connection Section */}
      <div className="wallet-section">
        <div className="wallet-info">
          <div className="wallet-details">
            {isConnected ? (
              <>
            <span className="wallet-address">
                  🔗 Wallet Connected: {userAddress?.slice(0, 8)}...{userAddress?.slice(-6)}
            </span>
            <span className="wallet-balance">
                  <button 
                    className="wallet-btn disconnect"
                    onClick={disconnectWallet}
                  >
                    Disconnect
                  </button>
            </span>
              </>
            ) : (
              <>
                <span className="wallet-address">
                  🔒 Wallet Not Connected
            </span>
                <span className="wallet-balance">
          <button 
            className="wallet-btn connect"
                    onClick={connectWallet}
          >
              🔗 Sync Wallet
          </button>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'collection' ? 'active' : ''}`}
          onClick={() => setActiveTab('collection')}
        >
          🎭 Full Collection
        </button>
        <button 
          className={`tab-btn ${activeTab === 'my-nfts' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-nfts')}
          disabled={!isConnected}
        >
          💼 My NFTs
        </button>
      </div>

      {/* Conditional Content Based on Active Tab */}
      {activeTab === 'collection' ? (
        <>
      {/* Layer Filter Section - New Visual Filter UI */}
      <div className="layer-filter-section">
        <div className="layer-filter-header">
          {/* <h2 className="layer-filter-title">Visual Layer Filter</h2> */}
          <p className="layer-filter-subtitle">Filter NFTs by selecting specific layer variants</p>
        </div>
        
        <div className="layer-filter-layout">
          {/* Left: Layer Bar */}
          <div className="layer-filter-left">
            <LayerBar
              layers={layers}
              activeLayer={activeLayer}
              onChange={handleLayerChange}
              isMobile={false}
            />
          </div>
          
          {/* Center: Variant Picker */}
          <div className="layer-filter-center">
            <VariantPicker
              layerKey={activeLayer}
              variants={layers[activeLayer] || []}
              valueMap={layerUI[activeLayer] || {}}
              onChange={(newValueMap) => handleVariantChange(activeLayer, newValueMap)}
              onlyShowCompatible={onlyShowCompatible}
              onToggleOnlyCompat={handleToggleOnlyCompat}
            />
          </div>
          
          {/* Right: Live Preview */}
          <div className="layer-filter-right">
            <LivePreview
              composedLayers={composedLayers}
              soloLayer={soloLayer}
              onToggleSolo={handleToggleSolo}
              hoveredVariant={hoveredVariant}
            />
          </div>
        </div>
        
        {/* Filter Chips */}
        <FilterChips
          chips={layerUI}
          onRemove={handleRemoveFilter}
          onClearAll={handleClearAll}
          onUndo={handleUndo}
          canUndo={layerHistory.length > 0}
        />
      </div>

      {/* Price Impact Simulator Section */}
      <div className="price-impact-section">
        <div className="impact-header">
          <h3>Price Impact Simulator</h3>
          <p>Simulate the price impact of buying multiple NFTs</p>
        </div>
        <div className="impact-simulator">
          <div className="simulator-controls">
            <div className="input-group">
              <label>Number of NFTs to buy:</label>
              <input
                type="number"
                min="1"
                max="100"
                value={priceImpactTake}
                onChange={(e) => setPriceImpactTake(parseInt(e.target.value) || 1)}
                className="impact-input"
                disabled={priceDataLoading || !priceDataAvailable}
              />
            </div>
            <button 
              onClick={handlePriceImpactCalculate}
              className="calculate-button"
              disabled={priceImpactLoading || priceDataLoading || !priceDataAvailable}
            >
              {priceImpactLoading ? (
                <>
                  <div className="button-spinner"></div>
                  Calculating...
                </>
              ) : priceDataLoading ? (
                <>
                  <div className="button-spinner"></div>
                  Loading Prices...
                </>
              ) : !priceDataAvailable ? (
                <>
                  <div className="button-spinner"></div>
                  Loading Prices...
                </>
              ) : (
                'Calculate Impact'
              )}
            </button>
          </div>
          {priceImpactLoading && (
            <div className="impact-loading">
              <div className="loading-spinner"></div>
              <p>Calculating price impact...</p>
            </div>
          )}
          {priceImpactResult && !priceImpactLoading && (
            <div className="impact-results">
              <div className="impact-grid">
                <div className="impact-card">
                  <div className="impact-label">Price Impact</div>
                  <div className="impact-value">{priceImpactResult.impact}%</div>
                </div>
                <div className="impact-card">
                  <div className="impact-label">Average Price</div>
                  <div className="impact-value">{formatXTZ(parseFloat(priceImpactResult.avgPrice))}</div>
                </div>
                <div className="impact-card">
                  <div className="impact-label">Total Cost</div>
                  <div className="impact-value">{formatXTZ(parseFloat(priceImpactResult.totalCost))}</div>
                </div>
                {/* <div className="impact-card">
                  <div className="impact-label">Savings vs Floor</div>
                  <div className="impact-value">{formatXTZ(parseFloat(priceImpactResult.savings))}</div>
                </div> */}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls Section */}
      <div className="controls-section">
        <div className="controls-row">
          <div className="controls-left">
            <div className="control-group">
              <input
                type="text"
                placeholder="Search NFTs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="control-group">
              <span className="control-label">Filter:</span>
              <select 
                value={filterBy} 
                onChange={(e) => handleFilterChange(e.target.value)}
                className="select-dropdown"
              >
                <option value="all">All NFTs</option>
                <option value="withPrice" disabled={!priceDataAvailable}>With Price</option>
                <option value="withoutPrice" disabled={!priceDataAvailable}>Without Price</option>
                <option value="top100">Top 100</option>
                <option value="top500">Top 500</option>
                <option value="rare">Rare (Top 50)</option>
              </select>
            </div>
          </div>
          <div className="controls-right">
            <div className="control-group">
              <span className="control-label">Sort by:</span>
              <select 
                value={sortBy} 
                onChange={(e) => handleSortChange(e.target.value)}
                className="select-dropdown"
              >
                <option value="rank">Rarity Rank</option>
                <option value="score">Rarity Score</option>
                <option value="name">Name</option>
                <option value="price" disabled={!priceDataAvailable}>Price</option>
              </select>
            </div>
            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
        <div className="nft-count">
          {loading ? (
            <span>🔄 Loading prices... Please wait</span>
          ) : (
            <span>
              Showing {currentNfts.length} of {filteredAndSortedNfts.length} NFTs, 
              sorted by {sortBy} {sortOrder === 'asc' ? 'ascending' : 'descending'}
                  {priceDataLoading && (
                    <span style={{ color: '#fbbf24', marginLeft: '8px' }}>
                      <span className="loading-spinner"></span>
                      Price data loading...
                    </span>
                  )}
                  {!priceDataAvailable && !priceDataLoading && ' (Price data unavailable)'}
                  {Object.keys(layerUI).some(layerKey => 
                    layerUI[layerKey] && Object.values(layerUI[layerKey]).some(value => value === 'only' || value === 'include' || value === 'exclude')
                  ) && ` (Layer filtered: ${getLayerFilteredCount()} NFTs match current filters)`}
            </span>
          )}
        </div>
            
            {/* Removed test button */}
      </div>

      {/* Download Section - Only visible to authorized wallet */}
      {isConnected && userAddress === "tz1PtTK4NT9Yy8Gsx3L63DiEkQPGrpVcCLNW" && (
        <div className="download-section">
          <button 
            onClick={() => downloadCurrentPageImages()}
            className="download-btn"
            disabled={currentNfts.length === 0}
          >
            📥 Download All Images ({currentNfts.length})
          </button>
          <span className="download-info">
            Downloads all NFT images currently displayed on this page
          </span>
        </div>
      )}

      {/* NFT Grid */}
      <div className="nft-grid">
        {currentNfts.map((nft) => (
              <NFTCard
            key={nft.id} 
                id={nft.id}
                title={nft.title}
                imageUrl={getImageUrl(nft)}
                rank={nft.rank}
                score={nft.score}
                price={nft.hasPrice ? nft.price : null}
                held={!nft.hasPrice}
                marketplaceUrl={`https://objkt.com/tokens/hicetnunc/${nft.id}`}
              />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-btn"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2)
            .map((page, index, array) => (
              <React.Fragment key={page}>
                {index > 0 && array[index - 1] !== page - 1 && (
                  <span className="page-btn" style={{ cursor: 'default', opacity: 0.5 }}>...</span>
                )}
                <button 
                  onClick={() => handlePageChange(page)}
                  className={`page-btn ${page === currentPage ? 'active' : ''}`}
                >
                  {page}
                </button>
              </React.Fragment>
            ))}
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next
          </button>
        </div>
          )}
        </>
      ) : (
        /* My NFTs Tab */
        <UserNFTs />
      )}

      {/* Portal-based Tooltip - Renders outside normal DOM hierarchy */}
      {showTooltip && createPortal(
        <div 
          className="portal-tooltip"
          style={{
            position: 'fixed',
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            transform: 'translateX(-50%)',
            zIndex: 999999999
          }}
        >
          <div className="tooltip-content">
            <span className="tooltip-icon">🚧</span>
            <div className="tooltip-text">
              <strong>Coming Soon!</strong>
              <p>You'll be able to sweep/buy NFTs and view your collection.</p>
            </div>
          </div>
          <div className="tooltip-arrow"></div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Rarity;



